import React, { FC, MouseEvent, useContext, useEffect, useState } from 'react'
import { UserSettingsInput } from '../components/UserSettingsInput'
import { useDispatch, useSelector } from 'react-redux'
import { AppState, Product, authCheckAccessToken } from '@news-mono/web-common'
import { UserSettingsButton } from '../components/UserSettingsButton'
import {
    ChangeNameFormData,
    useChangeName,
    validateName,
} from '../hooks/useChangeName'
import { MyAccountContext } from '../hooks/useMyAccountContext'
import { UserSettingProps } from '../UserSetting'
import { StyledUserSettingsForm } from '../components/UserSettingsForm.styled'
import { StyledButtonGroup } from '../components/UserSettingsButtonGroup.styled'
import { useProduct } from '../../__product'

export interface ChangeNameProps extends UserSettingProps {}

export const ChangeName: FC<ChangeNameProps> = ({ onEvent, log, config }) => {
    const dispatch = useDispatch()
    const { setToast, setMessageBanner, goBackToParent } =
        useContext(MyAccountContext)
    const { firstName, lastName, wanUserId, auth0UserId, userEmail } =
        useSelector((state: AppState) => state.authentication)

    const [submitting, setSubmitting] = useState<boolean>(false)
    const [name, setName] = useState<ChangeNameFormData>({
        firstName: firstName ?? '',
        lastName: lastName ?? '',
    })
    const product = useProduct()
    const isPn = product === Product.PerthNow

    const { mutateAsync: changeName } = useChangeName({
        firstName: name.firstName.trim(),
        lastName: name.lastName.trim(),
        userId: auth0UserId || wanUserId,
        email: userEmail,
    })

    async function onChangeSubmit(e: MouseEvent<HTMLElement>) {
        e.preventDefault()
        setMessageBanner(undefined)
        const validate = validateName(name)

        if (!validate.valid) {
            validate.message &&
                setMessageBanner({
                    text: validate.message,
                    type: 'error',
                })
        } else {
            setSubmitting(true)
            const res = await changeName()
            setSubmitting(false)
            if (res) {
                setToast({
                    text: 'Saved',
                    type: 'success',
                    show: true,
                })
                goBackToParent(false)

                // Refresh user info after updates
                dispatch(
                    authCheckAccessToken({
                        onEvent,
                        log,
                        config,
                        invocation: 'auto',
                        forceRefresh: true,
                        onInvocationEvent: onEvent,
                    }),
                )
            } else {
                setToast({
                    text: 'Failed to save.',
                    type: 'error',
                    show: true,
                })
            }
        }
    }

    function onCancel(e: MouseEvent<HTMLElement>) {
        e.preventDefault()
        goBackToParent(true)
    }

    return (
        <StyledUserSettingsForm>
            <UserSettingsInput
                id="first-name"
                label="First name"
                value={name.firstName}
                onChange={(firstName) => setName({ ...name, firstName })}
            />
            <UserSettingsInput
                id="last-name"
                label="Last name"
                value={name.lastName}
                onChange={(lastName) => setName({ ...name, lastName })}
                marginBottom={isPn ? 32 : undefined}
            />
            <StyledButtonGroup>
                <UserSettingsButton onClick={onCancel} btnType="muted">
                    CANCEL
                </UserSettingsButton>
                <UserSettingsButton
                    onClick={onChangeSubmit}
                    btnType="primary"
                    disabled={submitting}
                >
                    SAVE
                </UserSettingsButton>
            </StyledButtonGroup>
        </StyledUserSettingsForm>
    )
}
